import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {useEffect, useState} from 'react';
import {
  Box,
  // eslint-disable-next-line no-restricted-imports
  useToast,
} from '@chakra-ui/react';
import {useRouter} from 'next/router';
import {useTransactionDrawerContext} from '../../../context/transaction-drawer';
import {useCurrentAssetOverview} from '../../../hooks/current-asset-overview';
import {
  useGetActiveListingsQuery,
  useAddOfferMutation,
  usePayOfferMutation,
  Listing,
  useUserQuery,
} from '../../../graphql/generated';
import {NoPiecesAvailable} from '../overlays/no-pieces-available';
import {QuickBuyPayment} from '../overlays/quick-buy-payment';
import {BuyPieces} from '../overlays/buy-pieces';
import {TransactionDrawerSessionExpired} from './transaction-drawer-session-expired';
import {stripPublicKey} from '../../../utils/utils';
import {
  AddressForm,
  BuyerTermsAndConditionsForm,
} from '../forms/formValidationSchema';
import {
  BUYER_DRAWER_STEPS,
  MARKETPLACE_SESSION_TIMEOUT,
  Routes,
} from '../../../shared/consts';
import {mixpanel} from '../../../utils/mixpanel';
import {useIFrameModalContext} from '../../../context/iframe-modal';
import {BuyerTermsAndConditions} from '../overlays/buyer-terms-and-conditions';

const stripePromise = loadStripe(stripPublicKey);
let setIntervalCache: any;
const clearTimer = () => setIntervalCache && clearInterval(setIntervalCache);

// interface QuickBuyDrawerProps {}
export const PurchaseDrawer: React.FC = () => {
  const {
    drawerQuantity,
    drawerProduct,
    drawerListing,
    openDrawer,
    setDrawerLoading,
    resetDrawer,
    setPreventExit,
    updateQuantity,
  } = useTransactionDrawerContext();

  const {openModal} = useIFrameModalContext();
  const asset = useCurrentAssetOverview(drawerProduct as string);
  const user = useUserQuery();

  const assetListing = useGetActiveListingsQuery({
    variables: {assetId: drawerProduct as string},
    fetchPolicy: 'no-cache',
  });

  const router = useRouter();

  const [addOffer] = useAddOfferMutation();
  const [payOffer] = usePayOfferMutation();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<BUYER_DRAWER_STEPS>(BUYER_DRAWER_STEPS.BUY);
  const [sessionExpireTime, setSessionExpireTime] = useState(0);
  const [sessionIsExpired, setSessionIsExpired] = useState(false);
  const [sessionExpireWarning, setSessionExpireWarning] = useState(false);
  const [addressInfo, setAddressInfo] = useState<AddressForm>(
    {} as AddressForm,
  );
  const [termAndConditionsInfo, setTermAndConditionsInfo] =
    useState<BuyerTermsAndConditionsForm>({} as BuyerTermsAndConditionsForm);

  const [listing, setListing] = useState<Listing>();
  const [ownListing, setOwnListing] = useState(false);

  useEffect(() => {
    if (!assetListing.loading && !asset.loading && openDrawer) {
      const selectedListing = assetListing.data?.getActiveListings?.find(
        (item) => item && item.id === drawerListing,
      );

      if (selectedListing) {
        setListing(selectedListing as Listing);

        if (selectedListing.userId === user.data?.user.id) {
          setOwnListing(true);
        }
      }

      if (setDrawerLoading) setDrawerLoading(asset.loading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetListing.loading, asset.loading, openDrawer, resetDrawer]);

  useEffect(() => {
    if (sessionExpireTime) {
      clearTimer();
      let hasDisplayedSessionMessage = false;

      setIntervalCache = setInterval(() => {
        const now = new Date().getTime();
        const diff = sessionExpireTime - now;
        const timeRemaining = diff / 60000;

        if (
          timeRemaining < 2 &&
          timeRemaining > 1 &&
          !hasDisplayedSessionMessage
        ) {
          setSessionExpireWarning(true);
          hasDisplayedSessionMessage = true;
        } else if (timeRemaining < 0) {
          setSessionExpireWarning(false);
          setSessionIsExpired(true);
          clearTimer();
        }
      }, 30000);
    }

    return () => clearTimer();
  }, [sessionExpireTime]);

  useEffect(() => {
    if (setPreventExit) {
      if (step === BUYER_DRAWER_STEPS.PAYMENT) {
        setPreventExit(true);
      } else {
        setPreventExit(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleQuickBuyPiecesSubmit = () => {
    mixpanel.track(`proceed to payment ${drawerProduct}`);
    setIsLoading(true);
    const addOfferDetails = {
      assetId: drawerProduct as string,
      quantity: drawerQuantity as number,
      listingId: drawerListing as string,
    };

    addOffer({variables: addOfferDetails})
      .then((success) => {
        if (success.data?.addOffer === 'Success!') {
          const now = new Date();
          setSessionExpireTime(
            new Date(
              now.getTime() + MARKETPLACE_SESSION_TIMEOUT * 60000,
            ).getTime(),
          );
          setStep(BUYER_DRAWER_STEPS.TERMS_AND_CONDITIONS);
          setIsLoading(false);
        }
      })
      .catch((error: {message: any}) => {
        if (error && error.message) {
          setIsLoading(false);
          toast({
            status: 'error',
            title: 'Failed to reserve Pieces for purchase',
            description: error.message,
            duration: 3000,
          });
        }

        if (resetDrawer && updateQuantity) {
          resetDrawer();
          updateQuantity(0);
        }
      });
  };

  const handlePurchase = (
    stripeCardId: string,
    addressFormData: AddressForm,
  ) => {
    setIsLoading(true);

    const payOfferDetails = {
      assetId: drawerProduct as string,
      cardToken: stripeCardId,
      // addressInfo
      countryCode: addressFormData.country,
      city: addressFormData.townOrCity,
      postcode: addressFormData.postCode,
      lineOne: addressFormData.addressLine1,
      lineTwo: addressFormData.addressLine2,
      // termsAndConditionsInfo
      currencyConversionAndStripe:
        termAndConditionsInfo.currencyConversionAndStripe,
      noRefunds: termAndConditionsInfo.noRefunds,
      directFromSeller: termAndConditionsInfo.directFromSeller,
      termsAndConditions: termAndConditionsInfo.termsAndConditions,
    };

    payOffer({
      variables: payOfferDetails,
    })
      .then((result) => {
        if (result.data && result.data.payOffer) {
          if (resetDrawer) resetDrawer();
          mixpanel.track(
            `qucikbuy purchase ${drawerQuantity} ${drawerProduct}`,
          );

          const {status, url, paymentId} = result.data.payOffer;

          switch (status) {
            case 'validation_required': {
              if (url) {
                if (openModal) {
                  openModal('stripe', url, {paymentId});
                }
              } else {
                toast({
                  status: 'error',
                  title: 'Failed to make the purchase on Marketplace',
                  description: 'Please contact admin.',
                  duration: 3000,
                });
              }

              break;
            }

            default:
            case 'success': {
              router.push(Routes.QUEUE);
              break;
            }
          }
        }
      })
      .catch((error) => {
        if (error && error.message) {
          switch (error.message) {
            case 'Error: Wrong payment currency.': {
              toast({
                status: 'error',
                title: 'Sorry, buying pieces abroad isn’t supported yet',
                description:
                  'We’re looking to add this feature soon. For now, you can only buy pieces while in in the country of your billing address. Please contact info@showpiece.com if you need assistance!',
                duration: 3000,
              });
              break;
            }

            case 'Error: Session has expired please start again.': {
              setSessionIsExpired(true);
              break;
            }

            default: {
              toast({
                status: 'error',
                title: 'Failed to make the purchase on Marketplace',
                description: error.message,
                duration: 3000,
              });
              break;
            }
          }

          setAddressInfo({} as AddressForm);
          setIsLoading(false);
        }
      });
  };

  return (
    <>
      {step === BUYER_DRAWER_STEPS.BUY && (
        <BuyPieces
          asset={asset}
          onSubmit={() => handleQuickBuyPiecesSubmit()}
          isLoading={isLoading}
          listing={listing}
          ownListing={ownListing}
        />
      )}

      {step === BUYER_DRAWER_STEPS.PAYMENT && (
        <Elements stripe={stripePromise}>
          <QuickBuyPayment
            defaultValues={addressInfo}
            onSubmit={(formData, stripeId) => {
              setAddressInfo(formData);
              mixpanel.track(`submits card payment details ${drawerProduct}`);
              handlePurchase(stripeId, formData);
            }}
            isLoading={isLoading}
            onBackClick={() => setStep(BUYER_DRAWER_STEPS.TERMS_AND_CONDITIONS)}
          />
        </Elements>
      )}

      {step === BUYER_DRAWER_STEPS.NO_ITEMS && (
        <Box>
          <NoPiecesAvailable />
        </Box>
      )}

      {step === BUYER_DRAWER_STEPS.TERMS_AND_CONDITIONS && (
        <BuyerTermsAndConditions
          onSubmit={(dataInfo) => {
            setTermAndConditionsInfo(dataInfo);
            setStep(BUYER_DRAWER_STEPS.PAYMENT);
          }}
          onBackClick={() => setStep(BUYER_DRAWER_STEPS.BUY)}
        />
      )}

      <TransactionDrawerSessionExpired
        isActive={sessionIsExpired}
        title="Session Expired"
        details="Your session has expired and the Pieces you were holding have been lost."
        ctaLabel="Return to Quick Buy"
        onReturnClick={() => {
          setSessionIsExpired(false);
          setStep(BUYER_DRAWER_STEPS.BUY);
          setSessionExpireTime(0);
        }}
      />

      <TransactionDrawerSessionExpired
        isActive={sessionExpireWarning}
        title="Your session is about to expire"
        details="Your session will expire in <strong>1 minute</strong>"
        ctaLabel="Return to Payment"
        onReturnClick={() => {
          setSessionExpireWarning(false);
          if (step !== BUYER_DRAWER_STEPS.PAYMENT)
            setStep(BUYER_DRAWER_STEPS.PAYMENT);
        }}
      />
    </>
  );
};
